import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import QuantitySelector from "./QuantitySelector";
import { useTranslation } from "react-i18next";
import { calculateFinalPrice } from "./PriceCalculator";
import { HeartIcon } from "@heroicons/react/24/solid";

export default function EventTicketPage() {
  const { id } = useParams();
  const { t } = useTranslation();
  const [event, setEvent] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_BASE_URL}/api/event/${id}`)
      .then((res) => res.json())
      .then((data) => {
        setEvent(data);
        setLoading(false);
      })
      .catch(() => {
        setError("Événement introuvable.");
        setLoading(false);
      });
  }, [id]);

  const handleBuy = async () => {
    // Appel à ton endpoint Stripe pour créer une sessionww
    const response = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/eventticket/create-checkout-session`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          eventId: id,
          quantity,
        }),
      }
    );

    const data = await response.json();
    if (data.sessionUrl) {
      window.location.href = data.sessionUrl;
    }
  };

  if (loading) return <p className="text-center p-6">{t("loading")}</p>;
  if (error) return <p className="text-center text-red-500">{error}</p>;

  const getTotalWithTaxes = () => {
    const basePrice = Number(event.price);
    const calc = calculateFinalPrice(basePrice);

    return {
      subtotal: (calc.ticketPrice * quantity).toFixed(2),
      taxes: (calc.taxes * quantity).toFixed(2),
      total: (calc.totalPrice * quantity).toFixed(2),
    };
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 text-primary bg-gray-100">
      <div className="flex flex-col md:flex-row bg-white rounded-xl shadow overflow-hidden shadow-xl">
        {/* Image à gauche */}
        <div className="md:w-1/2 w-full h-64 md:h-auto">
          <img
            src={event.imageUrl}
            alt={event.name}
            className="w-full h-full object-cover shadow-lg"
          />
        </div>

        {/* Infos à droitew */}
        <div className="md:w-1/2 w-full p-6 flex flex-col space-y-6">
          <div>
            <h1 className="text-2xl font-bold mb-2">{event.name}</h1>
            {event.description && (
              <p className="text-gray-700 mb-4 whitespace-pre-line">
                {event.description}
              </p>
            )}
            <p className="mb-1">
              <strong>{t("ticketing.eventDate")}:</strong>{" "}
              {new Date(event.eventDate).toLocaleDateString("fr-CA", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </p>
            <p className="mb-1">
              <strong>{t("calendar.time")}:</strong>{" "}
              {new Date(event.eventDate).toLocaleTimeString("fr-CA", {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
            <p className="mb-1">
              <strong>{t("ticketing.priceLabel")}:</strong>{" "}
              {Number(event.price).toFixed(2)}$
            </p>
            {event.isFundraiser && (
              <div className="flex items-center gap-2 text-sm text-[#3a3a3a] rounded-full">
                <HeartIcon className="w-5 h-5 " />{" "}
                {t("ticketing.fundraiserTooltip") ||
                  "Cet événement est à but caritatif"}
              </div>
            )}
          </div>
          <div className="border-bottom border  w-1/2 justify-center"></div>
          {/* Sélecteur + bouton */}
          <div className="mt-4">
            <label className="font-semibold block mb-2">
              {t("modal.optionQuantity")}
            </label>
            <QuantitySelector
              max={10}
              value={quantity}
              onChange={setQuantity}
            />

            {event && (
              <div className="mt-4 text-sm text-gray-700 space-y-1">
                <p>
                  <strong>Sous-total :</strong> {getTotalWithTaxes().subtotal}$
                </p>
                <p>
                  <strong>Taxes :</strong> {getTotalWithTaxes().taxes}$
                </p>
                <p className="text-lg font-semibold text-primary">
                  Total : {getTotalWithTaxes().total}$
                </p>
              </div>
            )}

            <button
              onClick={handleBuy}
              className="bg-primary mt-4 text-white py-3 px-6 rounded-full hover:bg-primary/90 transition w-full md:w-auto"
            >
              {t("ticketing.buyTickets")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
